import { Component } from '@theme/component';

/** Slots are defined in the business's own local time, always. */
const BUSINESS_TIMEZONE = 'Europe/Stockholm';
const MAX_MONTHS_AHEAD = 6;

const FALLBACK_TIMEZONES = [
  'Europe/Stockholm', 'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Madrid',
  'Europe/Rome', 'Europe/Amsterdam', 'Europe/Oslo', 'Europe/Copenhagen', 'Europe/Helsinki',
  'Europe/Warsaw', 'Europe/Zurich', 'Europe/Dublin', 'Europe/Lisbon', 'Europe/Athens',
  'Europe/Moscow', 'Europe/Istanbul', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Sao_Paulo', 'America/Mexico_City', 'America/Toronto',
  'America/Vancouver', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Singapore',
  'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Jakarta',
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Pacific/Auckland',
  'Africa/Johannesburg', 'Africa/Cairo', 'Africa/Lagos', 'UTC',
];

// ---------------------------------------------------------------------------
// Swedish public holidays. Deliberately duplicated (rather than shared) with
// worker/booking-api/src/worker.js: the two run in unrelated JS runtimes with
// no build step wiring them together, and this is small enough that copying
// it is simpler than inventing a shared-module setup for one function. The
// server-side copy is the one that's actually authoritative; this copy only
// controls which calendar cells render as clickable.
// ---------------------------------------------------------------------------

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function addDays(date, days) {
  const result = new Date(date.getTime());
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

function midsummerEve(year) {
  for (let day = 19; day <= 25; day++) {
    const d = new Date(Date.UTC(year, 5, day));
    if (d.getUTCDay() === 5) return d;
  }
  throw new Error('unreachable');
}

function allSaintsDay(year) {
  for (let day = 31; day <= 37; day++) {
    const d = new Date(Date.UTC(year, 9, day));
    if (d.getUTCDay() === 6) return d;
  }
  throw new Error('unreachable');
}

const holidayCache = new Map();

function swedishHolidays(year) {
  let cached = holidayCache.get(year);
  if (cached) return cached;

  const easter = easterSunday(year);
  const eve = midsummerEve(year);
  const dates = [
    new Date(Date.UTC(year, 0, 1)),
    new Date(Date.UTC(year, 0, 6)),
    addDays(easter, -2),
    easter,
    addDays(easter, 1),
    new Date(Date.UTC(year, 4, 1)),
    addDays(easter, 39),
    addDays(easter, 49),
    new Date(Date.UTC(year, 5, 6)),
    eve,
    addDays(eve, 1),
    allSaintsDay(year),
    new Date(Date.UTC(year, 11, 24)),
    new Date(Date.UTC(year, 11, 25)),
    new Date(Date.UTC(year, 11, 26)),
    new Date(Date.UTC(year, 11, 31)),
  ];

  cached = new Set(dates.map((d) => d.toISOString().slice(0, 10)));
  holidayCache.set(year, cached);
  return cached;
}

/** @param {string} dateStr YYYY-MM-DD */
function isBookableWeekdayAndHoliday(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  return !swedishHolidays(y).has(dateStr);
}

// ---------------------------------------------------------------------------
// Timezone-aware formatting. Slot times are defined in BUSINESS_TIMEZONE;
// everything the visitor sees is converted into their chosen timezone.
// ---------------------------------------------------------------------------

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** UTC-offset in minutes of `timeZone` at the instant `date`. */
function getOffsetMinutes(timeZone, date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, /** @type {Record<string, string>} */ ({}));

  const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  // Real-world UTC offsets are always whole minutes; round off the
  // millisecond noise that `date`'s own sub-second precision introduces.
  return Math.round((asUtc - date.getTime()) / 60000);
}

/** The Date instant that corresponds to wall-clock `dateStr` `timeStr` inside `timeZone`. */
function zonedTimeToInstant(dateStr, timeStr, timeZone) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const offset = getOffsetMinutes(timeZone, new Date(guess));
  return new Date(guess - offset * 60000);
}

function formatOffsetLabel(offsetMinutes) {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  return `UTC${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

/** Renders a business-local slot time in the visitor's chosen timezone/format. */
function formatSlotLabel(dateStr, timeStr, timeZone, use12Hour) {
  const instant = zonedTimeToInstant(dateStr, timeStr, BUSINESS_TIMEZONE);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: use12Hour,
  }).format(instant);
}

function formatLongDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * @typedef {object} Refs
 * @property {HTMLElement} monthLabel
 * @property {HTMLButtonElement} prevMonthButton
 * @property {HTMLButtonElement} nextMonthButton
 * @property {HTMLElement} calendarGrid
 * @property {HTMLElement} dayLabel
 * @property {HTMLElement} timesList
 * @property {HTMLSelectElement} timezoneSelect
 * @property {HTMLButtonElement} format12Button
 * @property {HTMLButtonElement} format24Button
 * @property {HTMLElement} browseStep
 * @property {HTMLElement} formStep
 * @property {HTMLElement} successStep
 * @property {HTMLElement} summary
 * @property {HTMLFormElement} bookingForm
 * @property {HTMLInputElement} fullName
 * @property {HTMLInputElement} phone
 * @property {HTMLInputElement} email
 * @property {HTMLTextAreaElement} note
 * @property {HTMLElement} formError
 * @property {HTMLButtonElement} confirmButton
 *
 * @extends {Component<Refs>}
 */
export class BookingCalendar extends Component {
  requiredRefs = [
    'monthLabel', 'prevMonthButton', 'nextMonthButton', 'calendarGrid', 'dayLabel', 'timesList',
    'timezoneSelect', 'format12Button', 'format24Button', 'browseStep', 'formStep', 'successStep',
    'summary', 'bookingForm', 'fullName', 'phone', 'email', 'note', 'formError', 'confirmButton',
  ];

  connectedCallback() {
    super.connectedCallback();

    this.apiBase = (this.getAttribute('data-api-base') || '').replace(/\/$/, '');
    this.timeFormat = '24h';
    this.timezone = this.#detectTimezone();

    const today = new Date();
    this.baseMonth = { year: today.getFullYear(), month: today.getMonth() };
    this.viewMonth = { ...this.baseMonth };
    this.todayStr = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;

    this.selectedDate = null;
    this.selectedTime = null;
    this.availableSlots = null;

    if (!this.apiBase) {
      this.#showBookingUnavailable();
      return;
    }

    this.#populateTimezoneOptions();
    this.#renderCalendar();
    this.#renderTimes();
    this.#updateFormatButtons();
    this.#showStep('browse');
  }

  #showBookingUnavailable() {
    this.refs.calendarGrid.innerHTML = '';
    this.refs.timesList.innerHTML =
      '<p class="booking-calendar__error">Booking is not configured yet. Add the API endpoint in the theme editor.</p>';
  }

  #detectTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || BUSINESS_TIMEZONE;
    } catch {
      return BUSINESS_TIMEZONE;
    }
  }

  #populateTimezoneOptions() {
    const zones =
      typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : FALLBACK_TIMEZONES;
    const now = new Date();

    const withOffsets = zones
      .map((zone) => {
        try {
          return { zone, offset: getOffsetMinutes(zone, now) };
        } catch {
          return null;
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.offset - b.offset || a.zone.localeCompare(b.zone));

    this.refs.timezoneSelect.innerHTML = withOffsets
      .map(
        ({ zone, offset }) =>
          `<option value="${zone}"${zone === this.timezone ? ' selected' : ''}>(${formatOffsetLabel(offset)}) ${zone.replace(/_/g, ' ')}</option>`
      )
      .join('');

    if (![...this.refs.timezoneSelect.options].some((option) => option.value === this.timezone)) {
      this.timezone = BUSINESS_TIMEZONE;
      this.refs.timezoneSelect.value = BUSINESS_TIMEZONE;
    }
  }

  #renderCalendar() {
    const { year, month } = this.viewMonth;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = new Date(year, month, 1).getDay();

    this.refs.monthLabel.textContent = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(year, month, 1));

    let html = WEEKDAY_LABELS.map((label) => `<span class="booking-calendar__weekday">${label}</span>`).join('');

    for (let i = 0; i < startWeekday; i++) {
      html += '<span class="booking-calendar__cell booking-calendar__cell--empty" aria-hidden="true"></span>';
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${pad2(month + 1)}-${pad2(day)}`;
      const bookable = dateStr >= this.todayStr && isBookableWeekdayAndHoliday(dateStr);
      const isSelected = dateStr === this.selectedDate;

      if (bookable) {
        html += `<button type="button" class="booking-calendar__cell booking-calendar__cell--bookable${isSelected ? ' booking-calendar__cell--selected' : ''}" on:click="/selectDate?date=${dateStr}" aria-pressed="${isSelected}">${day}</button>`;
      } else {
        html += `<span class="booking-calendar__cell booking-calendar__cell--disabled" aria-disabled="true">${day}</span>`;
      }
    }

    this.refs.calendarGrid.innerHTML = html;

    const monthsAhead = (year - this.baseMonth.year) * 12 + (month - this.baseMonth.month);
    this.refs.prevMonthButton.disabled = monthsAhead <= 0;
    this.refs.nextMonthButton.disabled = monthsAhead >= MAX_MONTHS_AHEAD - 1;
  }

  #renderTimes() {
    if (!this.selectedDate) {
      this.refs.dayLabel.textContent = 'Select a date';
      this.refs.timesList.innerHTML =
        '<p class="booking-calendar__placeholder">Pick a date to see available times.</p>';
      return;
    }

    this.refs.dayLabel.textContent = formatLongDate(this.selectedDate);

    if (this.availableSlots === undefined) {
      this.refs.timesList.innerHTML = '<p class="booking-calendar__placeholder">Loading available times…</p>';
      return;
    }

    if (this.availableSlots === null) {
      this.refs.timesList.innerHTML =
        '<p class="booking-calendar__error">Couldn’t load times. Please try again.</p>';
      return;
    }

    if (this.availableSlots.length === 0) {
      this.refs.timesList.innerHTML =
        '<p class="booking-calendar__placeholder">No times left on this day — please pick another date.</p>';
      return;
    }

    const use12Hour = this.timeFormat === '12h';
    this.refs.timesList.innerHTML = this.availableSlots
      .map((time) => {
        const label = formatSlotLabel(this.selectedDate, time, this.timezone, use12Hour);
        return `<button type="button" class="booking-calendar__time" on:click="/selectTime?time=${time}">${label}</button>`;
      })
      .join('');
  }

  #updateFormatButtons() {
    const is12 = this.timeFormat === '12h';
    this.refs.format12Button.classList.toggle('is-active', is12);
    this.refs.format12Button.setAttribute('aria-pressed', String(is12));
    this.refs.format24Button.classList.toggle('is-active', !is12);
    this.refs.format24Button.setAttribute('aria-pressed', String(!is12));
  }

  #showStep(step) {
    this.refs.browseStep.hidden = step !== 'browse';
    this.refs.formStep.hidden = step !== 'form';
    this.refs.successStep.hidden = step !== 'success';
  }

  /** @param {{ date: string }} data */
  async selectDate({ date }) {
    this.selectedDate = date;
    this.selectedTime = null;
    this.availableSlots = undefined;
    this.#renderCalendar();
    this.#renderTimes();

    try {
      const response = await fetch(`${this.apiBase}/availability?date=${date}`);
      if (!response.ok) throw new Error(`${response.status}`);
      const body = await response.json();
      this.availableSlots = Array.isArray(body.available) ? body.available : [];
    } catch {
      this.availableSlots = null;
    }

    if (date === this.selectedDate) this.#renderTimes();
  }

  /** @param {{ time: string }} data */
  selectTime({ time }) {
    this.selectedTime = time;
    const label = formatSlotLabel(this.selectedDate, time, this.timezone, this.timeFormat === '12h');
    this.refs.summary.textContent = `${formatLongDate(this.selectedDate)} · ${label} · ${this.timezone.replace(/_/g, ' ')}`;
    this.refs.formError.hidden = true;
    this.#showStep('form');
    this.refs.fullName.focus();
  }

  backToCalendar() {
    this.#showStep('browse');
  }

  prevMonth() {
    this.#shiftMonth(-1);
  }

  nextMonth() {
    this.#shiftMonth(1);
  }

  #shiftMonth(delta) {
    let { year, month } = this.viewMonth;
    month += delta;
    if (month < 0) {
      month = 11;
      year -= 1;
    } else if (month > 11) {
      month = 0;
      year += 1;
    }
    this.viewMonth = { year, month };
    this.selectedDate = null;
    this.selectedTime = null;
    this.availableSlots = null;
    this.#renderCalendar();
    this.#renderTimes();
  }

  /** @param {{ format: string }} data */
  setFormat({ format }) {
    this.timeFormat = format === '12h' ? '12h' : '24h';
    this.#updateFormatButtons();
    this.#renderTimes();
  }

  /** @param {Event} event */
  changeTimezone(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);
    this.timezone = target.value;
    this.#renderTimes();
  }

  /** @param {SubmitEvent} event */
  async submitBooking(event) {
    event.preventDefault();

    const fullName = this.refs.fullName.value.trim();
    const phone = this.refs.phone.value.trim();
    const email = this.refs.email.value.trim();
    const note = this.refs.note.value.trim();

    if (!fullName || !phone || !email) {
      this.#setFormError('Please fill in your name, phone and email.');
      return;
    }

    this.refs.formError.hidden = true;
    this.refs.confirmButton.disabled = true;
    const originalLabel = this.refs.confirmButton.textContent;
    this.refs.confirmButton.textContent = 'Booking…';

    try {
      const response = await fetch(`${this.apiBase}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: this.selectedDate,
          time: this.selectedTime,
          timezone: this.timezone,
          fullName,
          phone,
          email,
          note,
        }),
      });

      if (response.status === 201) {
        this.#showStep('success');
        return;
      }

      if (response.status === 409) {
        this.#setFormError('That time was just booked by someone else — pick another below.');
        this.selectedTime = null;
        this.#showStep('browse');
        await this.selectDate({ date: this.selectedDate });
        return;
      }

      this.#setFormError('Something went wrong. Please try again.');
    } catch {
      this.#setFormError('Network error — please check your connection and try again.');
    } finally {
      this.refs.confirmButton.disabled = false;
      this.refs.confirmButton.textContent = originalLabel;
    }
  }

  #setFormError(message) {
    this.refs.formError.textContent = message;
    this.refs.formError.hidden = false;
  }
}

if (!customElements.get('booking-calendar-component')) {
  customElements.define('booking-calendar-component', BookingCalendar);
}
